import { ArrowRight, Database, MagnifyingGlass, Plus } from 'phosphor-react';
import { Button, Text } from '@typedream/ui';
import { useEventPlateId } from '@udecode/plate-core';
import {
  useCreateCollectionItemMutation,
  useCreateCollectionMutation,
  useGetCollectionItemsCountQuery,
  useListCollectionQuery,
  useUpdatePageCustomizationMutation,
} from '@/store';
import {
  CMSCollection,
  EmailCampaign,
  FormCollection,
  ICollectionDataItem,
  ICollectionIntegration,
  ICustomizationUpdateRequest,
  ProductCollection,
} from '@typedream/data';
import { useSpaceAndPageIDFromRouter } from '@/data/space/hooks';
import { v4 as uuidv4 } from 'uuid';
import { useEffect, useState } from 'react';
import levenshteinDistance from '@/utils/levenshteinDistance';
import { SearchBar } from '@/components/SearchBar';
import { useTPlateEditorRef } from '../../plate';

export function Connect({ goNext }: { goNext: () => void }) {
  const [spaceID, pageID] = useSpaceAndPageIDFromRouter();
  const editorID = useEventPlateId();
  const editor = useTPlateEditorRef(editorID as string);

  const { mutate: createCollection } = useCreateCollectionMutation();
  const { mutate: createCollectionItem } = useCreateCollectionItemMutation();
  const { mutate: updatePageCustomization } = useUpdatePageCustomizationMutation();
  const { data: collectionList } = useListCollectionQuery([spaceID]);
  const [collectionCounts, setCollectionCounts] = useState<{ [collectionID: string]: number }>({});

  // search functionality
  const [filteredCollectionList, setfilteredCollectionList] = useState([...(collectionList ?? [])]);

  useEffect(() => {
    // if collectionList changes, set filteredCollectionList to collectionList
    setfilteredCollectionList([...(collectionList ?? [])]);
  }, [collectionList]);

  // not a great search algo, but ok.
  const onChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const searchValue = e.target.value.toLowerCase();
    if (searchValue === '') {
      setfilteredCollectionList([...(collectionList ?? [])]);
      return;
    }

    // filter collectionList based on searchValue in based on its levenshtein distance
    const filtered = collectionList?.filter((c) => {
      let collectionName = '';
      if (c.type === 'cms') {
        collectionName = new CMSCollection(c).field.name.toLowerCase();
      } else if (c.type === 'form') {
        collectionName = new FormCollection(c).field.name.toLowerCase();
      } else if (c.type === 'product') {
        collectionName = (c as ProductCollection).field.name.toLowerCase();
      } else if (c.type === 'email') {
        collectionName = (c as EmailCampaign).field.name.toLowerCase();
      }
      const distance = levenshteinDistance(collectionName, searchValue);
      // check if f.name is a subset of searchValue
      const isSubset = collectionName.includes(searchValue) || searchValue.includes(collectionName);

      return isSubset || distance < 4;
    });

    setfilteredCollectionList(filtered);
  };

  const getItemCounts = async () => {
    const counts: { [collectionID: string]: number } = {};
    const promises = collectionList?.map(async (collection) => {
      const count = await useGetCollectionItemsCountQuery.run([spaceID, collection.id]).unwrap();
      counts[collection.id] = count;
    });
    await Promise.all(promises);
    setCollectionCounts(counts);
  };

  useEffect(() => {
    if (collectionList?.length > 0) {
      getItemCounts();
    }
  }, [collectionList]);

  const selectCMS = async (collectionID: string) => {
    const collectionDataID = uuidv4();

    // Add to page customization
    const colDataItem: ICollectionDataItem = {
      integration: ICollectionIntegration.TYPEDREAM_CMS,
      tableID: collectionID,
    };
    const customizationUpdateRequest: ICustomizationUpdateRequest = {
      customization_path: ['collection_data', collectionDataID],
      customization_value: colDataItem,
    };
    const updatedPage = await updatePageCustomization({
      spaceID,
      pageID,
      customizationUpdateRequests: [customizationUpdateRequest],
    });

    editor.emitter.emit('popup:on-cms-connect', collectionDataID);
    goNext();
  };

  const handleCreateNewCMS = async () => {
    const defaultCMSCollection = new CMSCollection();
    const { data: collection } = await createCollection(spaceID, defaultCMSCollection);

    if (!collection) {
      // handle error
      throw Error('Error creating a new CMS collection');
    }

    const defaultCMSItems = CMSCollection.getDefaultCMSItems(collection.id);
    const promises = defaultCMSItems.map(async (item) => {
      const createdItem = await createCollectionItem(spaceID, collection.id, item);
      return createdItem;
    });
    await Promise.all(promises);

    await selectCMS(collection.id);
  };

  if (collectionList?.length <= 0) {
    return (
      <>
        <div className="flex flex-col items-center justify-center gap-2">
          <div className="p-2 rounded-lg border border-solid border-neutral-200 dark:border-neutral-800">
            <Text variant="link" className="leading-none" as="div">
              <Database size={28} />
            </Text>
          </div>
          <Text variant="main" size="sm" weight="medium">
            Blogs (Collections)
          </Text>
          <Text variant="secondary" size="sm" weight="medium">
            This project doesn’t have any CMS collections yet
          </Text>
        </div>

        <div className="flex flex-col items-center justify-center gap-2">
          <Button leftIcon={<Plus />} onClick={handleCreateNewCMS}>
            Create Blog
          </Button>
        </div>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full">
      <Text variant="main" size="lg" weight="medium">
        Select a Collection
      </Text>

      <div className="flex items-center gap-2.5 w-full">
        <SearchBar
          className="h-full grow"
          placeholder="Search Collection"
          onChange={onChangeSearch}
          prefixComponent={<MagnifyingGlass size={20} className="text-secondary" />}
        />
        <Button leftIcon={<Plus />} onClick={handleCreateNewCMS}>
          New Blog
        </Button>
      </div>
      <div className="flex flex-col w-full gap-2 pb-48">
        {filteredCollectionList?.map((collection) => (
          <div
            className="flex px-4 py-2 items-center gap-2 rounded-lg bg-neutral-100 dark:bg-neutral-900"
            key={collection.id}
            role="none"
            onClick={() => {
              selectCMS(collection.id);
            }}
          >
            <div className="flex flex-col items-start justify-center gap-1 grow">
              <Text variant="main" size="sm" weight="medium">
                {collection.field?.name}
              </Text>
              <Text variant="secondary" size="2xs" weight="medium">
                {collection.type}
              </Text>
            </div>

            {collectionCounts[collection.id] !== undefined && (
              <Text variant="secondary" size="xs" weight="medium">
                {collectionCounts[collection.id]} items
              </Text>
            )}
            <Text variant="main" size="2xs" weight="medium" className="leading-none h-fit">
              <ArrowRight size={16} />
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
}
